import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, CircularProgress, Box, Typography, Chip, Button } from '@mui/material';
import {
  ArrowBack as BackIcon,
  Share as ShareIcon,
  Description as ReportIcon,
  Tune as ConfigureIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageLayout } from '../../PageLayout';
import { Card } from '../../Atoms/Card';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { EngagementEditorProvider, SideBySideView } from '../../Compounds/Advise/SideBySideView';
import { DiscardDraftDialog } from '../../Compounds/Advise/SideBySideView/DiscardDraftDialog';
import { useUnsavedDraftGuard } from '../../Compounds/Advise/SideBySideView/useUnsavedDraftGuard';
import {
  AdviseEngagement,
  EngagementStatus,
  AdviseSupplier,
  AdviseCustomer,
  EngagementConfiguration,
  SupplierSelectionSaved
} from '../../../types/advise';
import { engagementStatusLabel } from '../../../constants/advise';
import {
  getAdviseActiveEngagementSubject,
  getAdviseEngagementLoadingSubject,
  useAdviseEngagements
} from '../../../store/AdviseEngagement';
import { getAdviseSupplierSubject, upsertAdviseSuppliers, useAdviseSuppliers } from '../../../store/AdviseSupplier';
import { canEditEngagement } from './canEditEngagement';
import { usePendingSaves } from './usePendingSaves';
import { savesForPatch } from './engagementSaves';
import { useEngagementDiscovery } from './useEngagementDiscovery';
import { useEngagementFunctionalityMatrix } from './useEngagementFunctionalityMatrix';
import { useEngagementSupplierSelection } from './useEngagementSupplierSelection';
import { useEngagementCurrentSuppliers } from './useEngagementCurrentSuppliers';
import { useEngagementSupplierScoring } from './useEngagementSupplierScoring';
import { discoveryProgress, discoveryStepStatus } from '../../Compounds/Advise/SideBySideView/DiscoveryPanel.calc';
import {
  ratingProgress,
  ratingsByKey,
  supplierScoringStepStatus
} from '../../Compounds/Advise/SideBySideView/SupplierScoring.calc';
import { getUser, isInternalUser } from '../../../store/User';
import { fetchAdviseCustomerById } from '../../../Api/Advise/Customers';
import { useEngagementFeatureInput } from '../../../Api/Advise/useEngagementFeatureInput';
import { addError } from '../../../store/Error';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { ShareEngagementDialog } from '../../Compounds/Advise/ShareEngagementDialog';
import { ReportGenerator } from '../../Compounds/Advise/Reports';
import {
  ConfigurationExit,
  artifactsStepStatus,
  currentSuppliersStepStatus,
  functionalityMatrixStepStatus,
  quotesPricingStepStatus,
  supplierSelectionStepStatus,
  EngagementConfigurationScreen,
  isStepEnabled,
  StepsLoadError
} from '../../Compounds/Advise/EngagementSteps';
import { useEngagementConfiguration } from '../../../store/AdviseEngagementConfiguration';

export const AdviseAnalysisPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const engagementId = searchParams.get('id');

  const [engagement, setEngagement] = useState<AdviseEngagement | null>(null);
  const [suppliers, setSuppliers] = useState<AdviseSupplier[]>([]);
  const [customer, setCustomer] = useState<AdviseCustomer | null>(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const { fetchEngagementById } = useAdviseEngagements();
  const { fetchSuppliers } = useAdviseSuppliers();
  const { setSnack } = useSnackbar();

  const canEdit = canEditEngagement(engagement, engagementId);
  const {
    configuration,
    setConfiguration,
    failed: configurationFailed,
    retry
  } = useEngagementConfiguration(engagementId, { refresh: true });
  const canConfigure = canEdit && !!configuration?.can_configure;
  const configuring = canConfigure && searchParams.get('configure') === '1';
  const [quitting, setQuitting] = useState(false);
  const [openingReport, setOpeningReport] = useState(false);

  // Subscribe to store updates
  useEffect(() => {
    const engagementSub = getAdviseActiveEngagementSubject().subscribe((data) => setEngagement(data));
    const loadSub = getAdviseEngagementLoadingSubject().subscribe((val) => setLoading(val));
    const supplierSub = getAdviseSupplierSubject().subscribe((data) => setSuppliers(data));

    return () => {
      engagementSub.unsubscribe();
      loadSub.unsubscribe();
      supplierSub.unsubscribe();
    };
  }, []);

  // Fetch data on mount
  useEffect(() => {
    if (engagementId) {
      fetchEngagementById(engagementId);
    }
    fetchSuppliers();
  }, [engagementId]);

  // The engagement can belong to any account, so its customer is loaded by id rather than from the account picker.
  useEffect(() => {
    setCustomer(null);
    const customerId = engagement?.customer_id;
    if (!customerId) return;
    let current = true;
    const loadCustomer = async () => {
      const { data, error } = await fetchAdviseCustomerById(customerId);
      if (!current) return;
      addError('advise-customer', error?.message);
      setCustomer(data);
    };
    loadCustomer();
    return () => {
      current = false;
    };
  }, [engagement?.customer_id]);

  // Surfaces failures so optimistic UI can't mask a rejected save.
  const handleSaveError = useCallback(
    (message: string) => {
      addError('advise-engagement', message);
      setSnack({ message: 'Failed to save changes. Please try again.', open: true, type: 'error' });
    },
    [setSnack]
  );
  const pendingSaves = usePendingSaves(canEdit, handleSaveError);
  const { debouncedSave, flushSaves, awaitOnFlush } = pendingSaves;
  const { unsavedDraft, discardDialog } = useUnsavedDraftGuard();
  const { leave } = unsavedDraft;

  // Every way out to the dashboard leaves only once every edit is saved; a failed save keeps the user here.
  const leaveToDashboard = useCallback(
    () =>
      leave(async () => {
        setQuitting(true);
        if (await flushSaves()) navigate('/advise');
        else setQuitting(false);
      }),
    [flushSaves, leave, navigate]
  );

  const setConfiguring = useCallback(
    (on: boolean) =>
      setSearchParams(
        (params) => {
          const next = new URLSearchParams(params);
          if (on) next.set('configure', '1');
          else next.delete('configure');
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  const handleConfigurationSaved = useCallback(
    (saved: EngagementConfiguration, exit: ConfigurationExit) => {
      setConfiguration(saved);
      if (exit === 'quit') leaveToDashboard();
      else setConfiguring(false);
    },
    [leaveToDashboard, setConfiguration, setConfiguring]
  );

  const engagementRef = useRef(engagement);
  useEffect(() => {
    engagementRef.current = engagement;
  }, [engagement]);

  // Kept in the ref at once, so a second patch before the next render builds on this one.
  const showPatch = useCallback((patch: Partial<AdviseEngagement>) => {
    const current = engagementRef.current;
    if (!current) return null;
    const next = { ...current, ...patch };
    engagementRef.current = next;
    setEngagement(next);
    return next;
  }, []);

  // Every panel writes through this, so a read-only engagement sends nothing even where a panel leaves a control live.
  const editEngagement = useCallback(
    (patch: Partial<AdviseEngagement>) => {
      if (!canEdit) return;
      const next = showPatch(patch);
      if (!next) return;
      savesForPatch(patch).forEach(({ key, save }) => debouncedSave(key, () => save(next)));
    },
    [canEdit, debouncedSave, showPatch]
  );

  const discovery = useEngagementDiscovery(engagementId, canEdit, debouncedSave);
  const discoveryProgressByStep = useMemo(() => {
    if (!discovery.sections) return undefined;
    const { required, requiredAnswered } = discoveryProgress(discovery.sections);
    return { discovery: { answered: requiredAnswered, required } };
  }, [discovery.sections]);

  const functionalityMatrix = useEngagementFunctionalityMatrix(engagementId, canEdit, pendingSaves);

  // The shortlist saves through its own endpoint, so what it saved is shown without being sent again.
  const showSavedShortlist = useCallback(
    (saved: SupplierSelectionSaved) => {
      upsertAdviseSuppliers(saved.suppliers);
      showPatch({ selected_suppliers: saved.selected_suppliers });
    },
    [showPatch]
  );
  const supplierSelection = useEngagementSupplierSelection(engagement, canEdit, debouncedSave, showSavedShortlist);

  const currentSuppliers = useEngagementCurrentSuppliers(engagement, canEdit, awaitOnFlush, showPatch);
  const { currentSpend } = currentSuppliers;

  // The report reads the matrix from the API, so it opens only once every edit has saved. The current spend is
  // read again too: Configure and re-reads after a failed Current Suppliers save can change it unseen.
  const openReport = useCallback(async () => {
    setOpeningReport(true);
    if (await flushSaves()) {
      await currentSpend.refresh();
      setShowReport(true);
    }
    setOpeningReport(false);
  }, [currentSpend, flushSaves]);

  // The function ratings are UPSTACK's own view of each supplier, so the client's own users get no tab.
  const staff = isInternalUser(getUser());
  const supplierScoring = useEngagementSupplierScoring(engagementId, canEdit, pendingSaves);
  const { refresh: refreshScoring, scoring } = supplierScoring;
  // Waits for the configuration, so a turned-off step never loads and never raises a load error.
  const configurationKnown = !!configuration || configurationFailed;
  const scoringOn = staff && configurationKnown && isStepEnabled(configuration?.steps, 'supplier_scoring');
  useEffect(() => {
    if (scoringOn) refreshScoring();
  }, [scoringOn, refreshScoring]);
  // Loaded before its tab is opened, so the matrix's progress dot reads its saved selections.
  const { refresh: refreshMatrix } = functionalityMatrix;
  const matrixOn = configurationKnown && isStepEnabled(configuration?.steps, 'functionality_matrix');
  useEffect(() => {
    if (matrixOn) refreshMatrix?.();
  }, [matrixOn, refreshMatrix]);
  // Loaded here rather than by its view, so Supplier Selection's dot follows the imports and flags.
  const selectionOn = configurationKnown && isStepEnabled(configuration?.steps, 'supplier_selection');
  const featureInput = useEngagementFeatureInput(selectionOn ? engagementId : null);
  const scoringProgress = useMemo(
    () =>
      scoring &&
      ratingProgress(
        scoring.categories,
        scoring.suppliers.map((supplier) => supplier.id),
        ratingsByKey(scoring.ratings)
      ),
    [scoring]
  );

  const editor = useMemo(
    () =>
      engagement
        ? {
            engagement,
            suppliers,
            canEdit,
            editEngagement,
            discovery,
            functionalityMatrix,
            supplierSelection,
            featureInput,
            currentSuppliers,
            currentSpend,
            supplierScoring: staff ? supplierScoring : undefined,
            flushSaves,
            unsavedDraft
          }
        : null,
    [
      engagement,
      suppliers,
      canEdit,
      editEngagement,
      discovery,
      functionalityMatrix,
      supplierSelection,
      featureInput,
      currentSuppliers,
      currentSpend,
      staff,
      supplierScoring,
      flushSaves,
      unsavedDraft
    ]
  );

  const selectedSuppliers = useMemo(
    () => suppliers.filter((v) => engagement?.selected_suppliers?.includes(v.id)),
    [suppliers, engagement?.selected_suppliers]
  );

  // ---- Render ----

  if (!engagementId) {
    return (
      <PageLayout
        pageTitle="Advise Analysis"
        dataCy="advise-analysis-page">
        <Alert severity="warning">
          No engagement ID provided. Go back to{' '}
          <a
            href="/advise/engagements"
            className="underline">
            engagements
          </a>{' '}
          and select one.
        </Alert>
      </PageLayout>
    );
  }

  if (loading && !engagement) {
    return (
      <PageLayout
        pageTitle="Advise Analysis"
        dataCy="advise-analysis-page">
        <Box className="flex items-center justify-center p-12">
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!engagement) {
    return (
      <PageLayout
        pageTitle="Advise Analysis"
        dataCy="advise-analysis-page">
        <Alert severity="error">Engagement not found.</Alert>
      </PageLayout>
    );
  }

  const pageActions = (
    <Box className="flex items-center gap-2">
      <Chip
        label={engagementStatusLabel(engagement.status)}
        size="small"
        color={engagement.status === EngagementStatus.PUBLISHED ? 'success' : 'default'}
        variant="outlined"
      />
      {canConfigure && !configuring && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<ConfigureIcon />}
          onClick={() => leave(() => setConfiguring(true))}>
          Configure
        </Button>
      )}
      {canEdit && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<ShareIcon />}
          onClick={() => setShowShare(true)}>
          Share
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        startIcon={<ReportIcon />}
        disabled={openingReport}
        aria-label={openingReport ? 'Opening report' : undefined}
        onClick={openReport}>
        {openingReport ? <ComponentSpinner sx={{ height: '19px' }} /> : 'Report'}
      </Button>
      <Button
        size="small"
        startIcon={<BackIcon />}
        onClick={() => leave(() => (location.key === 'default' ? navigate('/advise') : navigate(-1)))}>
        Back
      </Button>
    </Box>
  );

  return (
    <PageLayout
      pageTitle={engagement.engagement_name || 'Advise Analysis'}
      pageActions={pageActions}
      dataCy="advise-analysis-page">
      <Box className="flex items-center gap-2 mb-4">
        {customer && (
          <Typography
            variant="body2"
            color="textSecondary">
            Customer: {customer.company_name}
          </Typography>
        )}
        <Typography
          variant="body2"
          color="textSecondary">
          {selectedSuppliers.length} supplier{selectedSuppliers.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {configurationFailed && (
        <Box className="mb-4">
          <StepsLoadError
            consequence="every tab shows and Configure is unavailable"
            onRetry={retry}
          />
        </Box>
      )}

      <Card className="bg-white p-5">
        {configuring && configuration ? (
          <EngagementConfigurationScreen
            engagementId={engagement.id}
            steps={configuration.steps}
            onSaved={handleConfigurationSaved}
            onExit={leaveToDashboard}
            exiting={quitting}
          />
        ) : (
          <EngagementEditorProvider value={editor}>
            <SideBySideView
              steps={configuration?.steps}
              stepStatus={{
                ...(discovery.sections && { discovery: discoveryStepStatus(discovery.sections) }),
                functionality_matrix: functionalityMatrixStepStatus(functionalityMatrix.criteria),
                supplier_selection: supplierSelectionStepStatus(
                  engagement.selected_suppliers,
                  featureInput.featureInput
                ),
                current_suppliers: currentSuppliersStepStatus(engagement.current_suppliers),
                quotes_pricing: quotesPricingStepStatus(engagement.uploaded_quotes),
                // No rule yet decides when this is done.
                roi_analysis: 'not_started',
                artifacts: artifactsStepStatus(engagement.data_room_files, engagement.data_room_note_files),
                ...(scoring &&
                  scoringProgress && {
                    supplier_scoring: supplierScoringStepStatus(scoring.suppliers, scoringProgress)
                  })
              }}
              stepFlow={
                canEdit
                  ? {
                      onBack: canConfigure ? () => leave(() => setConfiguring(true)) : undefined,
                      onSaveAndQuit: leaveToDashboard,
                      saving: quitting,
                      requiredProgress: { ...discoveryProgressByStep, supplier_scoring: scoringProgress ?? undefined },
                      // An answer ticks or clears matrix features on the server, so the matrix, and its dot, reload
                      // once Discovery is left; the refresh waits for the answers still saving.
                      onLeaveTab: (key) => {
                        if (key === 'discovery' && matrixOn) refreshMatrix?.();
                      }
                    }
                  : undefined
              }
            />
          </EngagementEditorProvider>
        )}
      </Card>

      {showShare && (
        <ShareEngagementDialog
          open={showShare}
          onClose={() => setShowShare(false)}
          engagement={engagement}
          onShared={(updated) => setEngagement(updated)}
        />
      )}

      <DiscardDraftDialog {...discardDialog} />
      {showReport && (
        <ReportGenerator
          engagement={engagement}
          suppliers={suppliers}
          customer={customer}
          open={showReport}
          onClose={() => setShowReport(false)}
        />
      )}
    </PageLayout>
  );
};
